import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Wordmark, MoonMark } from "@/components/MoonMark";
import { BurnsTicker } from "@/components/BurnsTicker";
import { ConnectMenu } from "@/components/ConnectMenu";
import { HERO_IMG, VENUE_META } from "@/lib/meta";
import { Flame, Signature, Megaphone, ShieldCheck } from "lucide-react";

const VENUES = ["pumpfun", "stonkfun", "fourmeme", "pons"];

const STEPS = [
  { icon: Signature, title: "Create on the pad", body: "Your wallet signs the mint. Wen Moon never holds the key." },
  { icon: Megaphone, title: "Connect X / TG / Discord", body: "Agents post the plan you approved. Chrome stays clean." },
  { icon: Flame, title: "Approve plan. Burns go public", body: "15% of the pad creator fee buys+burns $MOON. You keep 70%. All on-ledger." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-ink text-cream grain">
      {/* nav */}
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-line glass px-5 lg:px-10">
        <Wordmark />
        <nav className="hidden items-center gap-7 text-sm text-dust md:flex">
          <Link to="/burns" className="transition-colors hover:text-cream" data-testid="nav-public-burns">Moon Burns</Link>
          <Link to="/docs" className="transition-colors hover:text-cream" data-testid="nav-docs">Docs</Link>
          <Link to="/legal" className="transition-colors hover:text-cream" data-testid="nav-legal">Legal</Link>
        </nav>
        <ConnectMenu label="Connect wallet" testid="landing-connect-wallet" />
      </header>

      {/* hero */}
      <section className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 pt-16 pb-14 lg:grid-cols-2 lg:px-10 lg:pt-24">
        <div className="animate-fade-up">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-xs text-dust">
            <span className="h-1.5 w-1.5 rounded-full bg-pulp animate-soft-pulse" />
            Multi-venue launch + growth OS
          </div>
          <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Launch on any pad.<br />
            <span className="text-juice">Run</span> the campaign.<br />
            Feed the <span className="text-pulp">burn.</span>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-dust">
            Wallet in → pick Pump.fun, StonkFun, Four.meme or Pons → token + socials + budget.
            Launch free. Agents launch and post. You keep 70% of the pad creator fee — 15% runs the protocol, 15% buys+burns $MOON.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <ConnectMenu size="lg" label="Connect wallet" testid="hero-connect-wallet" showArrow />
            <Link to="/burns">
              <Button size="lg" variant="outline" className="border-line bg-surface text-cream hover:bg-raised" data-testid="hero-view-burns">
                <Flame size={16} className="mr-1.5 text-pulp" /> Watch the burns
              </Button>
            </Link>
          </div>
          <div className="mt-6 flex items-center gap-2 text-xs text-dust">
            <ShieldCheck size={14} className="text-pulp" /> You sign the launch. We never hold your mint key.
          </div>
        </div>

        <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
          <div className="absolute -inset-6 rounded-3xl bg-juice/10 blur-3xl" />
          <img src={HERO_IMG} alt="Wen Moon command center" className="relative rounded-2xl border border-line shadow-2xl" data-testid="hero-product-shot" />
        </div>
      </section>

      <BurnsTicker />

      {/* venue cards */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10">
        <div className="mb-2 flex items-end justify-between">
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">Four pads. One engine.</h2>
          <span className="hidden text-sm text-dust sm:block">Wen Moon is not a launchpad. It launches on the pad you pick.</span>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {VENUES.map((v) => {
            const m = VENUE_META[v];
            return (
              <div key={v} className="group rounded-2xl border border-line bg-surface p-5 transition-colors hover:border-juice/40" data-testid={`venue-card-${v}`}>
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg border border-line" style={{ color: m.color }}>
                  <MoonMark size={22} />
                </div>
                <h3 className="text-lg font-medium">{m.name}</h3>
                <p className="mt-1 font-mono text-xs text-dust">{m.chain}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* steps */}
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-10">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rounded-2xl border border-line bg-surface p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-raised text-juice">
                  <Icon size={20} />
                </div>
                <div className="mb-1 font-mono text-xs text-dust">0{i + 1}</div>
                <h3 className="text-lg font-medium">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-dust">{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* footer */}
      <footer className="border-t border-line bg-surface/40">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 py-8 text-sm text-dust md:flex-row md:items-center md:justify-between lg:px-10">
          <Wordmark />
          <p className="max-w-lg text-xs leading-relaxed">
            Wen Moon is a tool. Users sign every launch. No guaranteed volume, trending, or price.
            Not financial advice.
          </p>
          <div className="flex gap-5 text-xs">
            <Link to="/docs" className="hover:text-cream">Docs</Link>
            <Link to="/legal" className="hover:text-cream">Legal</Link>
            <Link to="/burns" className="hover:text-cream">Burns</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
